import { forwardRef } from 'react';

import { FormControl, Input, InputLabel, InputProps } from '@mui/material';

import { groupPhoneDigits } from '@/utils/stringUtils';

import type { Override } from '@/types/common';

type TPhoneNumberProps = Override<
  InputProps,
  {
    label: string;
  }
>;

/**
 * The phone library prints an Indian number with the trunk 0, as "062626 62662", or in five-digit
 * groups, as "62626 62662". This field shows the same groups as the closed card, "626 266 2662".
 * The stored value stays the full number. The library does not pass the selected country through.
 */
const toFieldDisplay = (value: unknown) => {
  if (typeof value !== 'string') {
    return value;
  }

  const digits = value.replace(/\D/g, '');
  const indianTrunk = digits.length === 11 && digits.startsWith('0');
  const fiveAndFive = /^\d{5} \d{5}$/.test(value);

  if (!indianTrunk && !fiveAndFive) {
    return value;
  }

  return groupPhoneDigits(digits);
};

export const PhoneNumber = forwardRef<HTMLInputElement, TPhoneNumberProps>(
  ({ label, value, ...props }, ref) => (
    <FormControl margin="none">
      <InputLabel>{label}</InputLabel>
      <Input inputRef={ref} {...props} value={toFieldDisplay(value)} />
    </FormControl>
  ),
);
